










































































































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import ContractProductsFiltersMixins from "@/mixins/provisioning/contractProductsFiltersMixins";
import Context from "@/mixins/context";

// Data/Enums
import contractProductRequestsFilters from "@/data/filters/contractProductRequests";
import contractProductRequestsSorters from "@/data/sorters/contractProductRequests";
import { DataModules } from "@/store/modules/data";
import { ProvisionRequestStatusCodes as PRSC } from "@/data/enums/contractProducts";
import clientFilters from "@/data/filters/client";

// Types
import { IFilter, ISorter } from "@/models/tables";
import { IClient } from "@/models/clients";
import { IContract, IContractProduct } from "@/models/contracts";
import { IProvisionRequest } from "@/models/provisioning";

@Component({
  components: {
    "product-image": () =>
      import("@/components/app/global/products/productImage.vue")
  }
})
export default class CProdProvRequestsTable extends Mixins(
  Context,
  FilteringSortingTableStore,
  ContractProductsFiltersMixins
) {
  // Props
  @Prop({ default: undefined })
  public contractId!: IContract["id"];

  @Prop({ default: undefined })
  public productId!: IContractProduct["id"];

  @Prop({ default: "" })
  public clientId!: IClient["id"];

  @Prop({ default: false })
  public withClientFilters!: number;

  @Prop({ default: false })
  public isStaged!: boolean;

  @Prop({ default: "CPRT1" })
  public declare id: string;

  // Data
  public dataModule: string = DataModules.PROVISIONING_CONTRACT_PRODUCTS;

  public filters: IFilter[] = [];
  public sorters: ISorter[] = [];

  public loading = true;
  public defaults = {
    sort: contractProductRequestsSorters[0]
  };

  public async created() {
    this.dataPayloadSettings = {
      contractId: this.contractId,
      productId: this.productId,
      ignoreStored: true
    };
    this.apiProps = {
      with: [
        "status",
        "results",
        "action_logs",
        "blueprint_function",
        "actor",
        "order_product",
        "order_product.product.image",
        "client",
        "configuration",
        "provider.logos"
      ].join(","),
      /** Filter to show only contract product requests, not eg. basket requests */
      "filter[order_product_type]": "contracts_product",
      /** Filter out draft requests */
      "filter[status.code|neq]": PRSC.PROVISION_REQUEST_DRAFT,
      with_staged_imports: this.isStaged ? 1 : 0
    };

    await this.getFiltersData();
    this.setFiltersAndSorters();
    await this.getData();
    this.startBusListener();
  }

  public startBusListener() {
    this.$bus.$on("refreshRequestTable", () => {
      this.refreshRequestsTable();
    });
  }

  // Getters
  public get showProductColumn() {
    return !this.productId;
  }

  public setFiltersAndSorters() {
    this.filters = this.$_.concat(
      contractProductRequestsFilters,
      !this.withClientFilters
        ? []
        : clientFilters().map(i => ({
            ...i,
            label: `${this.$t("_.client")}: ${i.label}`,
            key: `client.${i.key}`
          })),
      this.$_.map(this.createProvisionFilters(false), filter => {
        filter.key = filter.key
          .replace(
            "product.provision_blueprint_id",
            "blueprint_function.blueprint_id"
          )
          .replace("provision_provider_id", "provider_id")
          .replace("provision_configuration_id", "configuration_id"); // Update keys, because of different data structure than contract product reused code
        return filter;
      })
    );

    this.sorters = contractProductRequestsSorters;
  }

  // Override mixin method
  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(params, {
      params: {
        ...this.getFilterForClient()
      }
    });
  }

  public getFilterForClient() {
    return this.clientId ? { "filter[client_id]": this.clientId } : {};
  }

  public getDateTime(request: IProvisionRequest) {
    switch (request.status?.code) {
      case PRSC.PROVISION_REQUEST_SCHEDULED:
        return this.$_.get(request, "scheduled_for");
      case PRSC.PROVISION_REQUEST_DISPATCHED:
      case PRSC.PROVISION_REQUEST_SUCCESS:
        return this.$_.get(request, "dispatched_at");
      default: {
        return this.$_.get(request, "created_at");
      }
    }
  }

  public getDateDescMessage(request: IProvisionRequest) {
    switch (request.status?.code) {
      case PRSC.PROVISION_REQUEST_SCHEDULED:
        return this.$t("_.scheduled_for");
      case PRSC.PROVISION_REQUEST_DISPATCHED:
      case PRSC.PROVISION_REQUEST_SUCCESS:
        return this.$t("_.dispatched");
      default: {
        return this.$t("_.created");
      }
    }
  }

  public canCancelRequest(request: IProvisionRequest) {
    const statuses = [
      PRSC.PROVISION_REQUEST_PENDING,
      PRSC.PROVISION_REQUEST_DRAFT,
      PRSC.PROVISION_REQUEST_SCHEDULED,
      PRSC.PROVISION_REQUEST_DISPATCHED
    ];
    return (
      request.status &&
      statuses.includes(request.status.code) &&
      this.$userCan("cancel_provision_request")
    );
  }

  public canManuallyResolveRequest(request: IProvisionRequest) {
    return request.unresolved && this.$userCan("update_provision_request");
  }

  public refreshRequestsTable() {
    return this.getData({ ignoreStored: true });
  }

  public async openCancelRequestConfirm(request: IProvisionRequest) {
    if (!this.canCancelRequest(request)) return;

    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/openCancelRequestModal`,
      {
        requestId: request.id,
        onSuccess: () => {
          this.refreshRequestsTable();
        }
      }
    );
  }

  public async manuallyResolveRequest(request: IProvisionRequest) {
    if (!this.canManuallyResolveRequest(request)) return;
    try {
      await this.$store.dispatch(
        `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/updateRequest`,
        {
          requestId: request.id,
          data: {
            unresolved: false
          }
        }
      );
      this.$store.dispatch("toast/show", {
        message: this.$t("_.request_resolved")
      });
      this.refreshRequestsTable();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }

  public beforeDestroy() {
    this.$bus.$off("refreshRequestTable");
  }
}
